import { Component, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  PrTable,
  PrTableColumn,
} from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import {
  BuFeature,
  CustomerActivityDao,
} from '@app/dao/customer-activity/customer-activity.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { PledgeRead } from '../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../services/subscription-facade.service';
import { BottomSheetOverviewComponent } from './bottom-sheet-overview/bottom-sheet-overview.component';
type Mode = 'create' | 'edit';
export interface DialogData {
  productType: string;
  mode?: Mode;
  pledge?: PledgeRead;
}
@UntilDestroy()
@Component({
  selector: 'subscription-overview',
  templateUrl: './subscription-overview.component.html',
  styleUrls: ['./subscription-overview.component.scss'],
  standalone: false,
})
export class SubscriptionOverviewComponent implements OnInit {
  private _loading$ = this.subscriptionFacade.loading$;
  availableOperations = [];
  productsType = {
    SCPI: 'SCPI',
    PER: 'PER',
    PINEL: 'PINEL',
    ASSURANCE_VIE: 'assurance vie',
    FCPI: 'FCPI',
    FPCI: 'FPCI',
    BOND: 'Fonds Obligataires',
    GFV: 'Groupement foncier viticole',
    GFI: 'Groupements Forestiers d’Investissement',
    CONTINGENCY: 'Prévoyance',
    GFF: 'Groupement Foncier Forestier',
    TITRES_PARTICIPATIFS: 'Titres Participatifs',
    SOCIAL_SHARE: 'Parts Sociales',
    OBLIGATIONS: 'Obligations',
    DIVERSIFICATION_INVESTMENT: 'Investissement de diversification',
    COMPTE_TITRE: 'Compte Titre Ordinaire',
    PEA: 'PEA',
    CONTRAT_CAPI: 'Contrat de capitalisation',
    PEI: 'PEI',
    PEE: 'PEE',
    PERCO: 'PERCO',
    FIP: 'FIP',
    FIP_CORSE: 'FIP CORSE',
    FIP_DOMTOM: 'FIP DOM-TOM',
    FCPR: 'FCPR',
    FPS: 'FPS',
    GIRARDIN: 'GIRARDIN',
    INVESTISSEMENT_PME: 'INVESTISSEMENT PME',
    SOFICA: 'SOFICA',
    LMNP: 'LMNP',
    TANGIBLES: 'TANGIBLES',
    CROWDFUNDING: 'CROWDFUNDING',
    DEFISCALISATION: 'DEFISCALISATION',
    CAT: 'Compte À Terme',
    BORROWER_INSURANCE: 'Assurance Emprunteur',
  };
  StatusType = {
    OPEN: 'Ouvert',
    DRAFT: 'Brouillon',
    WAITING_SIGNATURE: 'En attente de signature du pack recommandation',
    WAITING_UNIVERSIGN_VALIDATION: 'En attente de validation Universign',
    WAITING_SIGNATURE_JURIDIC: 'En attente de signature du pack contractuel',
    WAITING_SEND_RECOMMENDATION_PACK:
      "En attente d'envoi du pack de recommandation",
    WAITING_SEND_MAPPING_PACK: "En attente d'envoi du pack de mapping",
    WAITING_SIGNATURE_MAPPING: 'En attente de signature du pack de mapping',
    INCOMPLETE: 'Incomplet',
    VALIDATION_BO: 'En attente de validation par le back-office',
    REJECTED_BO: 'Rejeté par le back-office',
    REJECTED_PARTNER: 'Rejeté par le partenaire',
    VALIDATED_BO: 'Validé par le back-office',
    VALIDATION_PARTNER: 'En attente de validation par le partenaire',
    CANCELED: 'Annulé',
    BROKEN: 'Casse',
    CLOSED: 'Clos',
    SIGNED: 'Signée',
  };
  table: PrTable;
  pledges$;
  columnsDefinition = [
    new PrTableColumn('Produit', val => this.formatProductInfo(val), {
      span: 2,
    }),
    new PrTableColumn('Opération', val => this.formatOperation(val), {
      span: 2,
    }),
    new PrTableColumn('Date de création', 'dateCreation', 'date', { span: 2 }),
    new PrTableColumn(
      'Personne(s) rattachée(s)',
      val => this.formatAttachedPersons(val),
      { span: 2 },
    ),
    new PrTableColumn('Status', val => this.getStatus(val), { span: 2 }),

    new PrTableColumn('', 'action', 'action', { class: 'text-right' }),
  ];
  canSubscribe$ = this.customerActivityDao.canUse$(BuFeature.SUBSCRIPTION);

  constructor(
    public subscriptionFacade: SubscriptionFacadeService,
    private referenceDao: ReferenceDao,
    private _bottomSheet: MatBottomSheet,
    private customerActivityDao: CustomerActivityDao,
  ) {}
  get loading$() {
    return this._loading$;
  }
  ngOnInit(): void {
    this.pledges$ = this.subscriptionFacade.pledgesWithCRUD$.pipe(
      tap(pledges => {
        this.initTable(pledges);
      }),
    );
    this.referenceDao.getAvailableOperations().subscribe(operations => {
      this.availableOperations = operations;
    });
  }
  initTable(pledges: any[]) {
    if (pledges?.length === 0) {
      this.table = new PrTable([], this.columnsDefinition);
      return;
    }
    const formattedPledges = this.formatPledges(pledges);
    const sortedPledges = this.sortPledgesByDateCreation(formattedPledges);
    this.table = new PrTable(sortedPledges, this.columnsDefinition);
  }
  formatPledges(pledges: PledgeRead[]): PledgeRead[] {
    return pledges.map(pledge => ({
      ...pledge,
      productType: pledge.productType || this.productsType.SCPI,
    }));
  }
  sortPledgesByDateCreation(pledges: PledgeRead[]): PledgeRead[] {
    return pledges.sort(
      (a, b) =>
        new Date(b.dateCreation).getTime() - new Date(a.dateCreation).getTime(),
    );
  }
  formatOperation(val: PledgeRead): string {
    const { operation = '' } = val;
    if (!operation) return '';
    const operationLabel = this.availableOperations.find(
      op => op.id === operation,
    )?.value;

    return operationLabel || operation;
  }
  formatProductInfo(val: PledgeRead): string {
    const productType =
      this.productsType?.[val?.productType] ||
      val?.productType ||
      this.productsType?.SCPI;
    const productName =
      val?.products?.length === 1
        ? ' - ' + val?.products?.[0]?.productName
        : '';
    return productType + productName;
  }

  formatAttachedPersons(val: PledgeRead): string {
    if (val.owners?.length > 1) {
      return `${val.owners[0]?.userFirstName}-${val.owners[1]?.userFirstName}`;
    } else {
      return val.owners[0]?.userFirstName;
    }
  }
  getStatus(val: PledgeRead): string {
    return (
      this.StatusType[val?.products?.[0]?.status] ||
      this.StatusType[val?.progress] ||
      val.progress
    );
  }

  delete(data: PledgeRead) {
    this.subscriptionFacade
      .deletePledge(data)
      .pipe(untilDestroyed(this))
      .subscribe(() => {});
  }
  edit(pledge: PledgeRead): void {
    this.subscriptionFacade.initOwner({ ...pledge });
  }
  openBottomSheet(): void {
    this._bottomSheet.open(BottomSheetOverviewComponent);
  }
  isPledgeStatusRemovable(pledge: PledgeRead) {
    const { progress, products } = pledge;
    const status = products?.[0]?.status || progress;
    return ['OPEN', 'DRAFT'].includes(status);
  }
}
